import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import momentDurationFormatSetup from 'moment-duration-format';
import queryString from 'query-string';
import styled from 'styled-components';

import ReadingDataReport from './readingData/readingDataReport';
import SurveyDataReport from './surveyData/surveyDataReport';
import AbandonedReadersReport from './abandonedReaders/abandonedReadersReport';
import ReaderJourneyFilterContainer from './readerJourneyFilterContainer';
import {
  clearReadingData,
  clearReviewData,
  clearSurveyData,
  clearAbandonedReadersData,
} from '../../../modules/report';

momentDurationFormatSetup(moment);

const ReportWrapper = styled.div`
  padding: 0 1em;
`;

const Tabs = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid var(--br-border);
  margin: 16px 0;

  button {
    padding: 10px 14px;
    border: 0;
    border-bottom: 3px solid transparent;
    background: transparent;
    color: var(--br-text);
    cursor: pointer;
    font: inherit;
  }

  button[aria-selected='true'] {
    border-bottom-color: var(--br-accent);
    color: var(--br-accent);
    font-weight: 700;
  }

  button:focus-visible {
    outline: 2px solid var(--br-focus);
    outline-offset: 2px;
  }
`;

const ReportOverview = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => () => {
    dispatch(clearReadingData());
    dispatch(clearSurveyData());
    dispatch(clearReviewData());
    dispatch(clearAbandonedReadersData());
  }, [dispatch]);

  const paneName = queryString.parse(location.search).pane;
  const tabs = [
    { key: 'reading', label: t('ReadingDataReport'), content: <ReadingDataReport /> },
    { key: 'survey', label: t('SurveyAnswersReport'), content: <SurveyDataReport /> },
    { key: 'abandoned', label: t('AbandonedReadersReport'), content: <AbandonedReadersReport /> },
  ];
  const active = tabs.find(tab => tab.key === paneName) || tabs[0];

  const goToPane = key => {
    const next = { ...queryString.parse(location.search), pane: key };
    history.push(`${location.pathname}?${queryString.stringify(next)}`);
  };

  const onTabKeyDown = (event, index) => {
    const nextIndex = event.key === 'ArrowRight'
      ? (index + 1) % tabs.length
      : event.key === 'ArrowLeft'
        ? (index - 1 + tabs.length) % tabs.length
        : event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? tabs.length - 1
            : null;
    if (nextIndex === null) return;
    event.preventDefault();
    event.currentTarget.parentElement.querySelectorAll('[role="tab"]')[nextIndex].focus();
    goToPane(tabs[nextIndex].key);
  };

  return (
    <ReportWrapper>
      <ReaderJourneyFilterContainer />
      <Tabs role="tablist" aria-label={t('Reports', 'Reports')}>
        {tabs.map((tab, index) => (
          <button
            key={tab.key}
            id={`report-tab-${tab.key}`}
            type="button"
            role="tab"
            aria-selected={active.key === tab.key}
            aria-controls="report-tab-panel"
            tabIndex={active.key === tab.key ? 0 : -1}
            onClick={() => goToPane(tab.key)}
            onKeyDown={event => onTabKeyDown(event, index)}
          >
            {tab.label}
          </button>
        ))}
      </Tabs>
      <div id="report-tab-panel" role="tabpanel" aria-labelledby={`report-tab-${active.key}`}>
        {active.content}
      </div>
    </ReportWrapper>
  );
};

export default ReportOverview;
